import {
  ModalDialog,
  ModalDialogActions,
  ModalDialogDescription,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogTitle,
} from '@/components/ModalDialog'
import { Button } from '@/components/Button'

export interface DeleteTaxRefundMethodDialogProps {
  /**
   * Display name of the tax refund method to delete.
   */
  displayName: string
  /**
   * Whether the dialog is open.
   */
  isOpen: boolean
  /**
   * Callback when the dialog should close.
   */
  onClose?: () => unknown
  /**
   * Callback when the confirm delete button is pressed.
   */
  onConfirmDeletePress?: () => unknown
  /**
   * Whether the delete operation is pending.
   */
  isDeletePending?: boolean
  /**
   * Test ID attribute.
   */
  testId?: string
  /**
   * Test ID attribute for the confirm delete button.
   */
  confirmDeleteTestId?: string
  /**
   * Test ID attribute for the cancel button.
   */
  cancelTestId?: string
}

/**
 * A dialog for confirming the deletion of a tax refund method.
 */
export function DeleteTaxRefundMethodDialog({
  isOpen,
  onClose,
  displayName,
  isDeletePending,
  onConfirmDeletePress,
  testId,
  confirmDeleteTestId,
  cancelTestId,
}: DeleteTaxRefundMethodDialogProps) {
  return (
    <ModalDialog
      isOpen={isOpen}
      onClose={onClose}
      isDismissable={!isDeletePending}
      testId={testId}
    >
      <ModalDialogHeader>
        <ModalDialogTitle>
          Permanently delete tax refund method?
        </ModalDialogTitle>
        <ModalDialogDescription>
          <p>
            Are you sure you want to permanently delete this{' '}
            <span className="text-strong">{displayName}</span> refund method?
          </p>
          <p>
            Please note that by deleting, it will still remain on any prior job
            where it has been provided as a tax refund method, but it won't show
            up in the list of existing tax refund methods on your account.
          </p>
        </ModalDialogDescription>
      </ModalDialogHeader>
      <ModalDialogFooter>
        <ModalDialogActions>
          <Button
            label="Yes, delete permanently"
            variant="default"
            onPress={onConfirmDeletePress}
            isPending={isDeletePending}
            testId={confirmDeleteTestId}
          />
          <Button
            label="No, cancel"
            variant="outline"
            onPress={onClose}
            isDisabled={isDeletePending}
            testId={cancelTestId}
          />
        </ModalDialogActions>
      </ModalDialogFooter>
    </ModalDialog>
  )
}
